import { useState } from 'react'
import { useIsFetching } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import type { OperationsNode } from '@sportninja/common/features/competition/operations/types/operations.types'
import { resolveTreeDrop } from '@sportninja/common/features/competition/operations/utils/resolveTreeDrop'
import { resolveDropPreview } from '@sportninja/common/features/competition/operations/utils/resolveDropPreview'
import type { PendingMove } from '@sportninja/common/features/competition/operations/utils/applyPendingMove'
import { useMoveTeam } from './useMoveTeam'

export interface OperationsDnd {
  handleDrop: (
    activeId: string,
    overId: string | null,
    after?: boolean
  ) => Promise<void>
  /**
   * Parent id -> child node ids in their dropped order. Enough for the GRID,
   * which shows one division's teams and so only ever reorders within a list.
   */
  localOrder: Record<string, string[]>
  /**
   * The same drop as a relocation. The TREE needs this rather than `localOrder`
   * because a team can land under a different parent, which a per-parent
   * ordering cannot express.
   */
  pendingMove: PendingMove | null
  /** Team ids with a move in flight, for the pending row treatment. */
  pendingIds: Set<string>
  isDragging: boolean
  setIsDragging: (dragging: boolean) => void
}

/**
 * The drag controller shared by the tree rail and the teams grid.
 *
 * TEAMS ONLY. Divisions and conferences are ordered through the inline Order
 * cell, which writes `sort_order` directly — dragging them was dropped because
 * that cell already does the job.
 *
 * `localOrder` is a deliberate departure from this feature's rule that the
 * server is the only source of truth. Every other mutation here invalidates on
 * settle and writes nothing optimistically, which a drag cannot do: the row
 * would visibly snap back to where it started until the refetch returned.
 *
 * It is released once the REFETCH lands, not once the mutation settles. Those
 * are different moments: `useMoveTeam` invalidates the tree query on settle but
 * does not await the refetch, so releasing on settle left a gap in which the
 * optimistic order was gone and the new server data had not arrived yet — the
 * row painted at its OLD position for a frame, which read as a flicker on a
 * same-division reorder and as a jump-then-correct across divisions.
 *
 * On failure it is released immediately, letting the refetched server order
 * show through.
 */
export const useOperationsDnd = ({
  nodes,
  rootScheduleId,
}: {
  nodes: OperationsNode[]
  rootScheduleId: string
}): OperationsDnd => {
  'use memo'

  const [localOrder, setLocalOrder] = useState<Record<string, string[]>>({})
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set())
  const [awaitingRefetch, setAwaitingRefetch] = useState(false)

  const { moveTeam } = useMoveTeam(rootScheduleId)

  // The tree refetch triggered by the move's invalidation. Watching it is what
  // lets the optimistic order survive until real data replaces it.
  const isFetchingTree = useIsFetching({
    queryKey: queryKeys.operations.tree(rootScheduleId),
  })

  // Release the moment that refetch finishes — not when the mutation settled.
  if (awaitingRefetch && isFetchingTree === 0) {
    setAwaitingRefetch(false)
    setLocalOrder({})
    setPendingMove(null)
    setPendingIds(new Set())
  }

  const handleDrop = async (
    activeId: string,
    overId: string | null,
    after = false
  ) => {
    setIsDragging(false)
    if (!overId) {
      return
    }

    const intent = resolveTreeDrop(nodes, activeId, overId, after)
    if (intent.kind === 'none') {
      return
    }

    // Paint the dropped order immediately. Handles the cross-division case too,
    // where the team leaves one list and joins another.
    setLocalOrder(resolveDropPreview(nodes, activeId, overId, after))
    setPendingMove({ activeId, overId, after })

    // Both id shapes: the grid rows key off the bare `teamId`, while the tree
    // rows key off the composite node id (`${scheduleId}-${teamId}`). Storing
    // one would leave the other's pending treatment silently dead.
    setPendingIds(new Set([intent.teamId, activeId]))

    try {
      await moveTeam({
        teamId: intent.teamId,
        fromScheduleId: intent.fromScheduleId,
        toScheduleId: intent.toScheduleId,
        placement: intent.placement,
        // The dropped row is its own confirmation — see `silent` on MoveArgs.
        silent: true,
      })
      // Hold the painted order until the refetch replaces it.
      setAwaitingRefetch(true)
    } catch {
      // `useMoveTeam` surfaces its own error toast. Release at once so the
      // server's true order shows through.
      setLocalOrder({})
      setPendingMove(null)
      setPendingIds(new Set())
    }
  }

  return {
    handleDrop,
    localOrder,
    pendingMove,
    pendingIds,
    isDragging,
    setIsDragging,
  }
}
